'use client';
import { motion, MotionProps } from 'framer-motion';
import { merge, omit } from 'lodash';

const BASE_SCROLL_REVEAL_DELAY = 0.4;
const VIEWPORT_ONCE = true;

type MotionPreset =
  | 'slide-down'
  | 'slide-up'
  | 'slide-left'
  | 'slide-right'
  | 'scale-in'
  | 'fade-in';

const defaultPresets: Record<MotionPreset, MotionProps> = {
  'slide-down': {
    initial: { opacity: 0, y: -50 },
    whileInView: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
      },
    },
  },

  'slide-up': {
    initial: { opacity: 0, y: 50 },
    whileInView: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
      },
    },
  },

  'slide-left': {
    initial: { opacity: 0, x: 50 },
    whileInView: {
      opacity: 1,
      x: 0,
      transition: {
        type: 'spring',
      },
    },
  },

  'slide-right': {
    initial: { opacity: 0, x: -50 },
    whileInView: {
      opacity: 1,
      x: 0,
      transition: {
        type: 'spring',
      },
    },
  },

  'scale-in': {
    initial: { opacity: 0, scale: 0.9 },
    whileInView: {
      opacity: 1,
      scale: 1,
      transition: {
        type: 'spring',
      },
    },
  },

  'fade-in': {
    initial: { opacity: 0 },
    whileInView: {
      opacity: 1,
      transition: {
        type: 'spring',
      },
    },
  },
};

export const Motion = (
  props: MotionProps & {
    preset: MotionPreset;
    children: React.ReactNode;
    className?: string;
    scrollReveal?: boolean;
  },
) => {
  const motionProps = omit(props, [
    'className',
    'children',
    'preset',
    'scrollReveal',
  ]);
  const { preset, children, className } = props;
  const defaultViewport = { once: VIEWPORT_ONCE };
  const defaultPreset = defaultPresets[preset];
  const mergedWhileInView: any = merge(
    {},
    defaultPreset.whileInView,
    motionProps.whileInView,
  );
  const delay = props.scrollReveal ? BASE_SCROLL_REVEAL_DELAY : 0;
  const animate = merge(mergedWhileInView, {
    transition: {
      delay: (mergedWhileInView.transition?.delay || 0) + delay,
    },
  });
  const animateProps = props.scrollReveal
    ? {
        whileInView: animate,
      }
    : {
        animate,
      };

  return (
    <motion.div
      className={className}
      {...motionProps}
      initial={merge({}, defaultPreset.initial, motionProps.initial)}
      viewport={merge({}, defaultViewport, motionProps.viewport)}
      {...animateProps}
    >
      {children}
    </motion.div>
  );
};
